import {
  BrushEventData,
  BrushTickValueType,
} from "@/components/organisms/Brush/Brush.types";
import {
  getTickValues,
  getNextEvent,
  getPreviousEvent,
} from "@/components/organisms/Brush/Brush.utils";
import { BrushCanvas } from "@/components/organisms/Brush/BrushCanvas";
import { BrushControls } from "@/components/organisms/Brush/BrushControls";
import { BrushCursor } from "@/components/organisms/Brush/BrushCursor";
import { BrushEvent } from "@/components/organisms/Brush/BrushEvent";
import { BrushTick } from "@/components/organisms/Brush/BrushTick";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import {
  useAnimationFrame,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { useCallback, useState } from "react";

export function Brush({
  duration: initialDuration = 10, // in seconds
  events = [],
  renderEvent = (event: BrushEventData, canvasWidth: number) => null,
  onAnimationFrame = () => null,
}: {
  duration?: number;
  events?: BrushEventData[];
  renderEvent?: (event: BrushEventData, canvasWidth: number) => React.ReactNode;
  onAnimationFrame?: (frameData: {
    currentTime: number;
    cursorTime: number;
    startTime: number;
    endTime: number;
    duration: number;
    position: number;
    mode: "live" | "archive";
    isPaused: boolean;
    isCatchingUp: boolean;
    width: number;
  }) => void;
}) {
  const [mode, setMode] = useState<"live" | "archive">("live");
  const [isPaused, setIsPaused] = useState(false);
  const [isCatchingUp, setIsCatchingUp] = useState(false);
  const [ticks, setTicks] = useState<BrushTickValueType[]>([]);
  const [width, setWidth] = useState(0);
  const ref = useResizeObserver((entry) => {
    setWidth(entry.width);
  });

  // Cursor motion values
  const cursorPlaybackSpeedMotionValue = useMotionValue(1);
  const cursorDurationMotionValue = useMotionValue(initialDuration * 1000);
  const cursorPositionMotionValue = useMotionValue(0.5);
  const cursorTimeMotionValue = useMotionValue(0);
  const cursorStartTimeMotionValue = useMotionValue(0);
  const cursorEndTimeMotionValue = useMotionValue(0);
  const cursorSpringMotionValue = useSpring(0, {
    mass: 3, // Increase mass to slow down
    stiffness: 30, // Lower stiffness to slow down
    damping: 15, // Slightly higher damping for no bounce
  });

  // Paused time motion value
  const pausedTimeMotionValue = useMotionValue(0);
  const pausedAtTimeMotionValue = useMotionValue(0);
  const currentTimeMotionValue = useMotionValue(Date.now());

  const startSpring = useCallback(() => {
    cursorSpringMotionValue.set(1);
    setIsCatchingUp(true);
    setTimeout(() => {
      cursorSpringMotionValue.set(0);
      setIsCatchingUp(false);
    }, 1000);
  }, [cursorSpringMotionValue]);

  // Check if ticks need updating
  const shouldUpdateTicks = useCallback(
    (updatedTicks: BrushTickValueType[]) => {
      const shouldUpdate =
        ticks.length === 0 ||
        (ticks.length !== 0 &&
          updatedTicks.length !== 0 &&
          updatedTicks[0].timestamp !== ticks[0].timestamp &&
          updatedTicks[updatedTicks.length - 1].timestamp !==
            ticks[ticks.length - 1].timestamp);
      if (shouldUpdate) {
        // explain what part of the boolean expression above is true
        console.log({
          ticksLength: ticks.length,
          updatedTicksLength: updatedTicks.length,
          updatedTicksFirstTimestamp: updatedTicks?.[0]?.timestamp,
          updatedTicksLastTimestamp:
            updatedTicks?.[updatedTicks.length - 1]?.timestamp,
          ticksFirstTimestamp: ticks?.[0]?.timestamp,
          ticksLastTimestamp: ticks?.[ticks.length - 1]?.timestamp,
        });
      }
      return shouldUpdate;
    },
    [ticks],
  );

  useTransform(() => {
    const duration = cursorDurationMotionValue.get();
    const cursorTime = cursorTimeMotionValue.get();
    const cursorStartTime =
      cursorTime - duration * cursorPositionMotionValue.get();
    const cursorEndTime = cursorStartTime + duration;

    cursorStartTimeMotionValue.set(cursorStartTime);
    cursorEndTimeMotionValue.set(cursorEndTime);

    console.log("cursorStartTime", cursorStartTime);

    // Calculate date range and update ticks
    const updatedTicks = getTickValues(cursorStartTime, cursorEndTime);

    if (shouldUpdateTicks(updatedTicks)) {
      setTicks(updatedTicks);
    }
  });

  // Use animation frame for smooth time progression
  useAnimationFrame(() => {
    const currentTime = Date.now();
    currentTimeMotionValue.set(currentTime);

    if (mode === "live") {
      if (isPaused) {
        throw new Error("Live mode cannot be in paused state");
      } else {
        if (isCatchingUp) {
          const springValue = cursorSpringMotionValue.get();
          const cursorTime = cursorTimeMotionValue.get();
          const newCursorTime =
            cursorTime + springValue * (currentTime - cursorTime);
          cursorTimeMotionValue.set(newCursorTime);
        } else {
          cursorTimeMotionValue.set(currentTime);
        }
      }
    } else if (mode === "archive") {
      if (isPaused) {
        // Do nothing, as scroll event handler kicks in when in this state
      } else {
        const pausedTime = pausedTimeMotionValue.get();
        const pausedAtTime = pausedAtTimeMotionValue.get();
        const diff =
          (currentTime - pausedAtTime) * cursorPlaybackSpeedMotionValue.get();
        cursorTimeMotionValue.set(pausedTime + diff);
      }
    }

    // Call the animation frame callback with current state
    const cursorTime = cursorTimeMotionValue.get();
    const startTime = cursorStartTimeMotionValue.get();
    const endTime = cursorEndTimeMotionValue.get();
    const duration = cursorDurationMotionValue.get();
    const position = cursorPositionMotionValue.get();

    onAnimationFrame({
      currentTime,
      cursorTime,
      startTime,
      endTime,
      duration,
      position,
      mode,
      isPaused,
      isCatchingUp,
      width,
    });
  });

  // Handle pause/play toggle
  const togglePause = useCallback(() => {
    const currentTime = currentTimeMotionValue.get();
    const cursorTime = cursorTimeMotionValue.get();

    if (mode === "archive") {
      if (isPaused) {
        setIsPaused(false);
        pausedAtTimeMotionValue.set(currentTime);
      } else {
        setIsPaused(true);
        pausedTimeMotionValue.set(cursorTime);
        pausedAtTimeMotionValue.set(currentTime);
      }
    } else if (mode === "live") {
      if (isPaused) {
        throw new Error("Live mode cannot be paused");
      } else {
        setMode("archive");
        setIsPaused(true);
        pausedTimeMotionValue.set(currentTime);
        pausedAtTimeMotionValue.set(currentTime);
      }
    }
  }, [mode, isPaused]);

  const toggleMode = useCallback(() => {
    const currentTime = currentTimeMotionValue.get();

    if (mode === "archive") {
      setMode("live");
      setIsPaused(false);
      setIsCatchingUp(true);
      startSpring();
      pausedTimeMotionValue.set(0);
      pausedAtTimeMotionValue.set(0);
    } else {
      setMode("archive");
      setIsPaused(true);
      pausedTimeMotionValue.set(currentTime);
      pausedAtTimeMotionValue.set(currentTime);
    }
  }, [mode]);

  // Handle scroll in both live and archive modes
  const handleScroll = useCallback(
    (deltaX: number) => {
      if (!(mode === "archive" && isPaused)) return;
      if (width > 0) {
        const duration = cursorDurationMotionValue.get();
        const timePerPixel = duration / width; // milliseconds per pixel
        const currentTime = cursorTimeMotionValue.get();
        const newTime = currentTime - deltaX * timePerPixel;
        cursorTimeMotionValue.set(newTime);
        pausedTimeMotionValue.set(newTime);
        pausedAtTimeMotionValue.set(newTime);
      }
    },
    [mode, cursorTimeMotionValue, width, cursorDurationMotionValue],
  );

  // Handle cursor movement
  const handleCursorMove = useCallback((clientX: number, rect: DOMRect) => {
    const relativeX = clientX - rect.left;
    const newPosition = Math.max(0, Math.min(1, relativeX / rect.width));
    cursorPositionMotionValue.set(newPosition);
  }, []);

  // Handle time change from scrollable controls
  const handleTimeChange = useCallback(
    (newTime: number) => {
      if (mode === "archive" && isPaused) {
        cursorTimeMotionValue.set(newTime);
        pausedTimeMotionValue.set(newTime);
        pausedAtTimeMotionValue.set(newTime);
      }
    },
    [
      mode,
      isPaused,
      cursorTimeMotionValue,
      pausedTimeMotionValue,
      pausedAtTimeMotionValue,
    ],
  );

  // Handle playback speed change from slider
  const handlePlaybackSpeedChange = useCallback((newSpeed: number) => {
    cursorPlaybackSpeedMotionValue.set(newSpeed);
    const currentTime = currentTimeMotionValue.get();
    const cursorTime = cursorTimeMotionValue.get();
    pausedTimeMotionValue.set(cursorTime);
    pausedAtTimeMotionValue.set(currentTime);
  }, []);

  // Handle next event navigation
  const handleNextEvent = useCallback(() => {
    const currentCursorTime = cursorTimeMotionValue.get();
    const nextEvent = getNextEvent(events, currentCursorTime);

    if (nextEvent) {
      cursorTimeMotionValue.set(nextEvent.timestamp);
      pausedTimeMotionValue.set(nextEvent.timestamp);
      pausedAtTimeMotionValue.set(nextEvent.timestamp);
    }
  }, [
    events,
    cursorTimeMotionValue,
    pausedTimeMotionValue,
    pausedAtTimeMotionValue,
  ]);

  // Handle previous event navigation
  const handlePreviousEvent = useCallback(() => {
    const currentCursorTime = cursorTimeMotionValue.get();
    const previousEvent = getPreviousEvent(events, currentCursorTime);

    if (previousEvent) {
      cursorTimeMotionValue.set(previousEvent.timestamp);
      pausedTimeMotionValue.set(previousEvent.timestamp);
      pausedAtTimeMotionValue.set(previousEvent.timestamp);
    }
  }, [
    events,
    cursorTimeMotionValue,
    pausedTimeMotionValue,
    pausedAtTimeMotionValue,
  ]);

  // Use the unified frequency mapping

  // Handle duration change from slider
  const handleDurationChange = (newDuration: number) => {
    cursorDurationMotionValue.set(newDuration);
  };

  return (
    <div ref={ref}>
      <BrushCanvas
        mode={mode}
        onCursorMove={handleCursorMove}
        onJumpToLive={toggleMode}
        onScroll={handleScroll}
        cursorPositionMotionValue={cursorPositionMotionValue}
        pausedTimeMotionValue={pausedTimeMotionValue}
      >
        {/* Fluid Timing Ticks */}
        {ticks.map((tick) => (
          <BrushTick
            key={`${tick.timestamp}`}
            tick={tick}
            startTimeMotionValue={cursorStartTimeMotionValue}
            endTimeMotionValue={cursorEndTimeMotionValue}
            durationMotionValue={cursorDurationMotionValue}
          />
        ))}

        {/* Event Markers */}
        {events.map((event, index) => {
          return (
            <BrushEvent
              key={`event-${event.timestamp}-${index}`}
              event={event}
              startTimeMotionValue={cursorStartTimeMotionValue}
              endTimeMotionValue={cursorEndTimeMotionValue}
              width={width}
              renderEvent={renderEvent}
            />
          );
        })}

        {/* Cursor */}
        <BrushCursor
          cursorPositionMotionValue={cursorPositionMotionValue}
          cursorDurationMotionValue={cursorDurationMotionValue}
          cursorTimeMotionValue={cursorTimeMotionValue}
          currentTimeMotionValue={currentTimeMotionValue}
          width={width}
        />
      </BrushCanvas>

      {/* Brush Clock */}
      <BrushControls
        cursorPlaybackSpeedMotionValue={cursorPlaybackSpeedMotionValue}
        cursorDurationMotionValue={cursorDurationMotionValue}
        cursorTimeMotionValue={cursorTimeMotionValue}
        startTimeMotionValue={cursorStartTimeMotionValue}
        endTimeMotionValue={cursorEndTimeMotionValue}
        onTogglePause={togglePause}
        onToggleMode={toggleMode}
        onTimeChange={handleTimeChange}
        onDurationChange={handleDurationChange}
        onPlaybackSpeedChange={handlePlaybackSpeedChange}
        onNextEvent={handleNextEvent}
        onPreviousEvent={handlePreviousEvent}
        isPaused={isPaused}
        isLive={mode === "live"}
        startSpring={() => null}
      />
    </div>
  );
}
